import { useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, Divider, Input, Message } from 'semantic-ui-react';
import { Button } from '~/components';
import { usePromiseWatcher } from '~/hooks';
import {
  sendTwoFactorEmailCode,
  verifyTwoFactorBackupCode,
  verifyTwoFactorEmailCode,
  verifyTwoFactorPasskey,
} from '~/api/auth';
import AuthForm from '../components/AuthForm';
import redirectAfterLogin from './redirectAfterLogin';

export default function Verify2FA() {
  const methods = useMemo(() => {
    const raw = new URLSearchParams(window.location.search).get('methods');
    return raw ? raw.split(',') : ['email'];
  }, []);
  const hasPasskey = methods.includes('passkey');

  const [rememberDevice, setRememberDevice] = useState(false);
  const [code, setCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  const { execute: usePasskey, status: passkeyStatus } = usePromiseWatcher(
    async () => redirectAfterLogin(await verifyTwoFactorPasskey(rememberDevice)),
    { loading: null, success: null, error: 'Passkey verification failed. Please try again.' },
    [rememberDevice]
  );

  const { execute: sendCode, status: sendStatus } = usePromiseWatcher(
    async () => {
      await sendTwoFactorEmailCode();
      setCodeSent(true);
    },
    { loading: 'Sending a code to your email', success: 'Code sent', error: 'Could not send a code.' },
    []
  );

  const { execute: verifyCode, status: verifyStatus } = usePromiseWatcher(
    async () => {
      const user = useBackup
        ? await verifyTwoFactorBackupCode(code.trim(), rememberDevice)
        : await verifyTwoFactorEmailCode(code.trim(), rememberDevice);
      redirectAfterLogin(user);
    },
    { loading: null, success: null, error: 'That code was not valid. Please try again.' },
    [code, rememberDevice, useBackup]
  );

  // For email-primary users (no passkey), send a code as soon as the screen
  // loads so they aren't forced to click "Email me a code" first. Passkey users
  // opt in via the button, so we don't email them unprompted.
  const autoSent = useRef(false);
  useEffect(() => {
    if (autoSent.current || hasPasskey) return;
    autoSent.current = true;
    sendCode();
  }, [hasPasskey, sendCode]);

  const busy =
    passkeyStatus === 'pending' || verifyStatus === 'pending' || verifyStatus === 'success';

  return (
    <AuthForm header="Two-step verification">
      {hasPasskey && !useBackup && (
        <>
          <Button
            fluid
            secondary
            size="large"
            content="Use a passkey"
            loading={passkeyStatus === 'pending'}
            disabled={busy}
            onClick={usePasskey}
          />
          <Divider horizontal>or</Divider>
        </>
      )}

      {!useBackup && (
        <>
          <Button
            fluid
            outlined
            size="large"
            content={codeSent ? 'Resend email code' : 'Email me a code'}
            loading={sendStatus === 'pending'}
            disabled={busy}
            onClick={sendCode}
          />
          {sendStatus === 'pending' && !codeSent && (
            <Message info size="tiny" content="Sending a verification code to your email…" />
          )}
          {codeSent && (
            <Message
              info
              size="tiny"
              content="We emailed you a 6-digit code. It expires in 10 minutes — enter it below, or resend above."
            />
          )}
        </>
      )}

      <Input
        fluid
        size="large"
        style={{ marginTop: 'var(--small)' }}
        icon={useBackup ? 'shield' : 'lock'}
        iconPosition="left"
        placeholder={useBackup ? 'Backup recovery code' : '6-digit code'}
        value={code}
        autoComplete="one-time-code"
        onChange={(_e, { value }) => setCode(value)}
      />

      <Checkbox
        label="Remember this device for 30 days"
        checked={rememberDevice}
        onChange={(_e, { checked }) => setRememberDevice(!!checked)}
        style={{ marginTop: 'var(--small)' }}
      />

      <Button
        fluid
        secondary
        size="large"
        style={{ marginTop: 'var(--small)' }}
        disabled={code.trim().length === 0 || busy}
        loading={verifyStatus === 'pending' || verifyStatus === 'success'}
        onClick={verifyCode}
      >
        Verify
      </Button>

      <Message className="auth-page__footer-message">
        <Button
          variant="link"
          style={{ marginTop: 0 }}
          content={useBackup ? 'Use a passkey or email code instead' : 'Use a backup recovery code'}
          onClick={() => {
            setUseBackup((v) => !v);
            setCode('');
          }}
        />
      </Message>
    </AuthForm>
  );
}
